import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  TextInput, 
  //DatePicker,
  //DatePickerInput,
  Dropdown,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PGDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 3, 
  lastSavedStep = 0,
  onStepChange,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  console.log('🔍 PGDetails (Step 2) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    dataKeys: Object.keys(data),
    hasAttributes: !!data?.attributes
  });

  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  const FIELD_MAP = useMemo(() => ({
    pgPlaceAvailableFor: "pg_place_available_for",
    pgPreferredGuests: "pg_preferred_guests",
    pgAvailableFrom: "pg_available_from",
    pgFoodIncluded: "pg_food_included",
    pgFoodOptions: "pg_food_option",
    pgRules: "pg_hostel_rules",
    pgGateClosingTime: "pg_gate_closing_time",
    pgDescription: "pg_description",
  }), []);
  
  const [form, setForm] = useState({
    pgPlaceAvailableFor: "",
    pgPreferredGuests: "",
    pgAvailableFrom: "",
    pgFoodIncluded: "",
    pgFoodOptions: [],
    pgRules: [],
    pgGateClosingTime: "",
    pgDescription: "",
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Time state for 12-hour format
  const [timeHour, setTimeHour] = useState("12");
  const [timeMinute, setTimeMinute] = useState("00");
  const [timePeriod, setTimePeriod] = useState("AM");
  
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
          value: a.value
        }));
    },
    [attributeValues, FIELD_MAP]
  );
  
  const foodOptions = useMemo(() => {
    return getOptions("pgFoodOptions");
  }, [getOptions]);

const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  const pgRulesOptions = useMemo(() => {
    return getOptions("pgRules");
  }, [getOptions]);

  // TIME OPTIONS
  const hourOptions = useMemo(() => {
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    return hours.map(hour => ({
      id: String(hour).padStart(2, '0'),
      text: String(hour).padStart(2, '0'),
      label: String(hour).padStart(2, '0')
    }));
  }, []);

  const minuteOptions = useMemo(() => {
    const minutes = ["00", "15", "30", "45"];
    return minutes.map(minute => ({
      id: minute,
      text: minute,
      label: minute
    }));
  }, []);

  const periodOptions = useMemo(() => [
    { id: "AM", text: "AM", label: "AM" },
    { id: "PM", text: "PM", label: "PM" }
  ], []);

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // Handle PG/Hostel Rules based on gender selection
  const handlePlaceAvailableChange = useCallback((value) => {
    updateField("pgPlaceAvailableFor", value);
    
    // Auto-select rules based on gender selection
    if (value === "Female") {
      // Find and select "No Boy's Entry" rule (with apostrophe)
      const noBoysEntryRule = pgRulesOptions.find(rule => 
        rule.label.toLowerCase() === "no boy's entry" || 
        rule.label.toLowerCase().includes("boy's")
      );
      if (noBoysEntryRule) {
        setForm(prev => ({
          ...prev,
          pgRules: [String(noBoysEntryRule.id)]
        }));
      }
    } else if (value === "Male") {
      // Find and select "No Girl's Entry" rule (with apostrophe)
      const noGirlsEntryRule = pgRulesOptions.find(rule => 
        rule.label.toLowerCase() === "no girl's entry" || 
        rule.label.toLowerCase().includes("girl's")
      );
      if (noGirlsEntryRule) {
        setForm(prev => ({
          ...prev,
          pgRules: [String(noGirlsEntryRule.id)]
        }));
      }
    } else if (value === "Anyone") {
      // When Anyone is selected, clear the auto-selected rules
      setForm(prev => {
        const currentRules = prev.pgRules || [];
        const filteredRules = currentRules.filter(ruleId => {
          const rule = pgRulesOptions.find(r => String(r.id) === ruleId);
          if (rule) {
            const ruleLabel = rule.label.toLowerCase();
            // Remove both gender-specific rules when switching to Anyone
            return !ruleLabel.includes("boy's") && !ruleLabel.includes("girl's");
          }
          return true;
        });
        return { ...prev, pgRules: filteredRules };
      });
    }
  }, [updateField, pgRulesOptions]);

  // Get filtered rules based on selection
  const getFilteredRules = useMemo(() => {
    const selectedGender = form.pgPlaceAvailableFor;
    
    if (selectedGender === "Female") {
      // Hide "No Girl's Entry" rule when Female is selected
      return pgRulesOptions.filter(rule => 
        !rule.label.toLowerCase().includes("girl's")
      );
    } else if (selectedGender === "Male") {
      // Hide "No Boy's Entry" rule when Male is selected
      return pgRulesOptions.filter(rule => 
        !rule.label.toLowerCase().includes("boy's")
      );
    } else {
      // Show all rules when Anyone is selected
      return pgRulesOptions;
    }
  }, [form.pgPlaceAvailableFor, pgRulesOptions]);

  useEffect(() => {
    if (!data?.attributes) {
      console.log('💧 No attributes data found in PGDetails');
      return;
    }

    console.log('💧 PGDetails hydrating form with data:', data.attributes);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.attributes[apiKey];
      if (raw == null) {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: raw value =`, raw, 'type:', typeof raw);

if (field === "pgFoodOptions" || field === "pgRules") {
  let values = [];

  // Convert API value into an array
  if (Array.isArray(raw)) {
    values = raw;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        values = parsed;
      } else {
        values = raw
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }
    } catch {
      values = raw
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
  } else if (raw != null) {
    values = [raw];
  }

  // Convert API IDs/names -> option IDs
  const options = getOptions(field);

  incoming[field] = values
    .map((value) => {
      const matchedOption = options.find(
        (option) =>
          String(option.id) === String(value) ||
          String(option.label).trim().toLowerCase() ===
            String(value).trim().toLowerCase()
      );

      return matchedOption ? String(matchedOption.id) : null;
    })
    .filter(Boolean);

  console.log(`  - ${field}: mapped values =`, incoming[field]);
} else if (field === "pgGateClosingTime") {
        incoming[field] = String(raw);
        
        // Parse time from stored value
        if (raw) {
          const timeString = String(raw);
          const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (timeMatch) {
            setTimeHour(timeMatch[1].padStart(2, '0'));
            setTimeMinute(timeMatch[2]);
            setTimePeriod(timeMatch[3].toUpperCase());
          } else {
            // Try to parse 24-hour format
            const simpleMatch = timeString.match(/(\d{1,2}):(\d{2})/);
            if (simpleMatch) {
              let hour = parseInt(simpleMatch[1]);
              const minute = simpleMatch[2];
              const period = hour >= 12 ? 'PM' : 'AM';
              hour = hour % 12 || 12;
              
              setTimeHour(String(hour).padStart(2, '0'));
              setTimeMinute(minute);
              setTimePeriod(period);
            }
          }
        }
      } else if (field === "pgFoodIncluded") {
        if (raw === true || raw === 1 || raw === "true" || raw === "1" || raw === "yes" || raw === "Yes") {
          incoming[field] = "Yes";
        } else if (raw === false || raw === 0 || raw === "false" || raw === "0" || raw === "no" || raw === "No") {
          incoming[field] = "No";
        } else {
          incoming[field] = String(raw);
        }
      } else if (field === "pgPlaceAvailableFor") {
        if (typeof raw === 'string') {
          incoming[field] = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
        } else {
          incoming[field] = String(raw);
        }
      } else {
        incoming[field] = String(raw);
      }
    });

    console.log('💧 PGDetails incoming form data:', incoming);
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      console.log('💧 PGDetails new form state:', newForm);
      return newForm;
    });
  }, [data, FIELD_MAP]);
  
  const toggleFoodOption = useCallback((foodId) => {
    setForm((prev) => {
      const currentOptions = prev.pgFoodOptions || [];
      const foodIdStr = String(foodId);
      const updatedOptions = currentOptions.includes(foodIdStr)
        ? currentOptions.filter(id => id !== foodIdStr)
        : [...currentOptions, foodIdStr];
      return { ...prev, pgFoodOptions: updatedOptions };
    });
    setTouched((prev) => ({ ...prev, pgFoodOptions: true }));
  }, []);

  const togglePgRule = useCallback((ruleId) => {
    setForm((prev) => {
      const currentRules = prev.pgRules || [];
      const ruleIdStr = String(ruleId);
      const updatedRules = currentRules.includes(ruleIdStr)
        ? currentRules.filter(id => id !== ruleIdStr)
        : [...currentRules, ruleIdStr];
      return { ...prev, pgRules: updatedRules };
    });
    setTouched((prev) => ({ ...prev, pgRules: true }));
  }, []);

  const isFoodOptionSelected = useCallback(
    (foodId) => {
      return form.pgFoodOptions.includes(String(foodId));
    },
    [form.pgFoodOptions]
  );

  const isPgRuleSelected = useCallback(
    (ruleId) => {
      return form.pgRules.includes(String(ruleId));
    },
    [form.pgRules]
  );
    
  const updateTime = useCallback((type, value) => {
    if (type === "hour") {
      setTimeHour(value);
    } else if (type === "minute") {
      setTimeMinute(value);
    } else if (type === "period") {
      setTimePeriod(value);
    }
    const timeString = `${type === "hour" ? value : timeHour}:${type === "minute" ? value : timeMinute} ${type === "period" ? value : timePeriod}`;
    updateField("pgGateClosingTime", timeString);
  }, [timeHour, timeMinute, timePeriod, updateField]);

  // VALIDATION
  const getFieldError = useCallback((field) => {
    const v = form[field];

    if (field === "pgPlaceAvailableFor" && !v) {
      return "Place availability is required";
    }
    if (field === "pgPreferredGuests" && !v) {
      return "Preferred guests is required";
    }
    // Removed validation for pgAvailableFrom
    if (field === "pgFoodIncluded" && !v) {
      return "Food inclusion is required";
    }

    return null;
  }, [form]);

  const hasErrors = useCallback(() => {
    return Object.keys(form).some((field) => getFieldError(field));
  }, [form, getFieldError]);

  // USE COMMON FIELDS
  const { renderDropdown } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur: () => {},
    updateField,
    getOptions,
  });

  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 PGDetails handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    setTouched(Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    if (hasErrors()) {
      setError("please fill  all required fields");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 2...');
        uuid = await createProperty(form);
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "pgFoodOptions" || field === "pgRules") {
          if (Array.isArray(v) && v.length > 0) {
            payload[apiKey] = v.map(id => Number(id)); // Convert to numbers
          }
        } else if (field === "pgFoodIncluded") {
          payload[apiKey] = v === "Yes";
        } else if (field === "pgGateClosingTime") {
          const timeString = `${timeHour}:${timeMinute} ${timePeriod}`;
          payload[apiKey] = timeString;
        } else if (field === "pgPlaceAvailableFor") {
          payload[apiKey] = v.toLowerCase();
        } else if (field === "pgPreferredGuests") {
          payload[apiKey] = Number(v);
        } else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving PG details:', payload);
      await saveSectionAttributes(uuid, payload, form, "pg_details");

      setSuccess("PG details saved successfully!");
      
      onSaveSuccess?.(uuid, form, currentStep);
      
      onComplete?.(form);

      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save PG details");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    hasErrors,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext,
    timeHour,
    timeMinute,
    timePeriod
  ]);
  
  // BUTTON HANDLERS
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    handleSave(true);
  }, [handleSave]);
  
  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column sm={4} md={8} lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* PLACE AVAILABLE FOR - REQUIRED */}
              <FormGroup>
                <Grid>
                  <Column lg={16}>
                    <FormGroup legendText={requiredLabel("Place is available for")}>
                      <RadioButtonGroup
                        name="pgPlaceAvailableFor"
                        valueSelected={form.pgPlaceAvailableFor}
                        onChange={handlePlaceAvailableChange}
                        orientation="horizontal"
                      >
                        <RadioButton id="male" labelText="Male" value="Male" />
                        <RadioButton id="female" labelText="Female" value="Female" />
                        <RadioButton id="anyone" labelText="Anyone" value="Anyone" />
                      </RadioButtonGroup>
                      {touched.pgPlaceAvailableFor && getFieldError("pgPlaceAvailableFor") && (
                        <p style={{ color: '#da1e28', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          {getFieldError("pgPlaceAvailableFor")}
                        </p>
                      )}
                    </FormGroup>
                  </Column>
                </Grid>
              </FormGroup>

              {/* PREFERRED GUESTS & AVAILABLE FROM - REQUIRED */}
              <FormGroup>
                <Grid>
                  <Column lg={4}>
                    {renderDropdown("pgPreferredGuests", "Preferred Guests", true)}
                  </Column>
<Column lg={8}>
  <FormGroup legendText="Available From">
    <TextInput
      id="pgAvailableFrom"
      type="date"
      labelText=""
      value={form.pgAvailableFrom || ""}
      onChange={(e) => updateField("pgAvailableFrom", e.target.value)}
      onBlur={() => setTouched((prev) => ({ ...prev, pgAvailableFrom: true }))}
      disabled={saving}
    />
  </FormGroup>
</Column>
                </Grid>
              </FormGroup>

              {/* FOOD INCLUDED - REQUIRED */}
              <FormGroup>
                <Grid>
                  <Column lg={16}>
                    <FormGroup legendText={requiredLabel("Food Included ")}>
                      <RadioButtonGroup
                        name="pgFoodIncluded"
                        valueSelected={form.pgFoodIncluded}
                        onChange={(value) => updateField("pgFoodIncluded", value)}
                        orientation="horizontal"
                      >
                        <RadioButton id="food-yes" labelText="Yes" value="Yes" />
                        <RadioButton id="food-no" labelText="No" value="No" />
                      </RadioButtonGroup>
                      {touched.pgFoodIncluded && getFieldError("pgFoodIncluded") && (
                        <p style={{ color: '#da1e28', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          {getFieldError("pgFoodIncluded")}
                        </p>
                      )}
                    </FormGroup>
                  </Column>
                </Grid>
              </FormGroup>

              {/* FOOD OPTIONS - SHOW WHEN FOOD IS YES */}
              {form.pgFoodIncluded === "Yes" && foodOptions.length > 0 && (
                <FormGroup legendText="Food Options">
                  <Grid>
                    {foodOptions.map((food) => (
                      <Column key={food.id} sm={4} md={4} lg={4}>
                        <Checkbox
                          id={`food-${food.id}`}
                          labelText={food.label}
                          checked={isFoodOptionSelected(food.id)}
                          onChange={() => toggleFoodOption(food.id)}
                          disabled={saving}
                        />
                      </Column>
                    ))}
                  </Grid>
                </FormGroup>
              )}

              {/* PG RULES - FILTERED BASED ON SELECTION */}
              <FormGroup legendText="PG / Hostel Rules">
                <Grid>
                  {getFilteredRules.length > 0 ? (
                    getFilteredRules.map((rule) => (
                      <Column key={rule.id} sm={4} md={4} lg={4}>
                        <Checkbox
                          id={`rule-${rule.id}`}
                          labelText={rule.label}
                          checked={isPgRuleSelected(rule.id)}
                          onChange={() => togglePgRule(rule.id)}
                          disabled={saving}
                        />
                      </Column>
                    ))
                  ) : (
                    <Column lg={16}>
                      <p style={{ color: '#6f6f6f', fontStyle: 'italic' }}>
                        No rules available for configuration
                      </p>
                    </Column>
                  )}
                </Grid>
              </FormGroup>

              {/* GATE CLOSING TIME */}
              <FormGroup>
                <Grid>
                  <Column lg={10}>
                    <FormGroup legendText="Gate Closing Time">
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "6px", flexWrap: "wrap" }}>
                        {/* Hour Dropdown */}
                        <div style={{ minWidth: "110px" }}>
                          <Dropdown
                            id="time-hour"
                            label="Hour"
                            hideLabel
                            items={hourOptions}
                            itemToString={(item) => item?.text || ""}
                            selectedItem={hourOptions.find((h) => h.id === timeHour) || hourOptions[0]}
                            onChange={({ selectedItem }) => updateTime("hour", selectedItem?.id || "12")}
                            disabled={saving}
                          />
                        </div>
                        <span style={{ fontSize: "1.2rem", color: "#525252", marginBottom: "6px" }}>
                          :
                        </span>
                        {/* Minute Dropdown */}
                        <div style={{ minWidth: "110px" }}>
                          <Dropdown
                            id="time-minute"
                            label="Minute"
                            hideLabel
                            items={minuteOptions}
                            itemToString={(item) => item?.text || ""}
                            selectedItem={minuteOptions.find((m) => m.id === timeMinute) || minuteOptions[0]}
                            onChange={({ selectedItem }) => updateTime("minute", selectedItem?.id || "00")}
                            disabled={saving}
                          />
                        </div>
                        {/* AM/PM Dropdown */}
                        <div style={{ minWidth: "110px" }}>
                          <Dropdown
                            id="time-period"
                            label="AM/PM"
                            hideLabel
                            items={periodOptions}
                            itemToString={(item) => item?.text || ""}
                            selectedItem={periodOptions.find((p) => p.id === timePeriod) || periodOptions[0]}
                            onChange={({ selectedItem }) => updateTime("period", selectedItem?.id || "AM")}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </FormGroup>
                  </Column>
                </Grid>
              </FormGroup>

              {/* DESCRIPTION - OPTIONAL */}
              <FormGroup>
                <Grid>
                  <Column lg={16}>
                    <TextArea
                      id="pgDescription"
                      labelText="Description"
                      value={form.pgDescription || ""}
                      onChange={(e) => updateField("pgDescription", e.target.value)}
                      placeholder="Write a few lines about your property, something which is special and makes your property stand out. Please do not mention your contact details in any format."
                      rows={5}
                      disabled={saving}
                    />
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default PGDetails;